import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MapPin, Clock, Heart, Calendar, Briefcase, Truck, ArrowRight, Star } from 'lucide-react';
import { SiteHeader, SiteFooter, WhatsAppButton } from './RestaurantSite';
import { SEO, SCHEMA_TRAITEUR } from '../../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&family=Manrope:wght@300;400;500;600;700&display=swap";

// ══════ TRAITEUR HOME ══════
export function TraiteurHome() {
  const nav = useNavigate();
  const [sections, setSections] = useState({});

  useEffect(() => {
    fetch(`${API}/api/site/settings`).then(r => r.json()).then(s => setSections(s.sections || {})).catch(() => {});
  }, []);

  const showPrestations = sections.traiteur_prestations !== false;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',Georgia,serif]" data-testid="traiteur-home">
      <SEO title="Traiteur Evenementiel" description="Le Mas Traiteur a Saint-Laurent-de-la-Salanque. Mariages, anniversaires, repas d'entreprise, plateaux repas. Cuisine catalane et mediterraneenne. Devis gratuit." path="/site/traiteur" schema={SCHEMA_TRAITEUR} />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="traiteur" />

      {/* Hero */}
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/traiteur/hero.jpg)' }} />
        <div className="absolute inset-0 bg-[#0d9488]/60" />
        <div className="relative z-10 text-center px-6 max-w-3xl">
          <p className="text-white/60 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-5">Traiteur & Evenements</p>
          <h1 className="text-4xl md:text-6xl font-light text-white leading-tight mb-2">Sublimez vos</h1>
          <h1 className="text-4xl md:text-6xl italic text-white/90 leading-tight mb-6">evenements</h1>
          <p className="text-white/60 text-base font-['Manrope',sans-serif] leading-relaxed mb-8 max-w-xl mx-auto">
            Mariages, baptemes, receptions d'entreprise... Notre equipe de traiteurs met tout son savoir-faire au service de vos moments les plus precieux.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => nav('/site/traiteur/contact')}
              className="border-2 border-white text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors"
              data-testid="traiteur-devis-btn">
              Demander un devis
            </button>
            {showPrestations && (
            <button onClick={() => nav('/site/traiteur/prestations')}
              className="border-2 border-white/50 text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors"
              data-testid="traiteur-prestations-btn">
              Nos Prestations
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="py-24 bg-gray-50/50">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">Nos Services</p>
          <h2 className="text-center text-4xl font-light mb-16">Pour tous vos evenements</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: 'Mariages', desc: 'Sublimez votre jour unique' },
              { icon: Calendar, title: 'Evenements', desc: 'Baptemes, communions, anniversaires' },
              { icon: Briefcase, title: 'Entreprises', desc: 'Cocktails et receptions professionnelles' },
              { icon: Truck, title: 'Livraison', desc: 'Dans toute la region 66' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-100 p-8 text-center hover:shadow-md transition-shadow" data-testid={`traiteur-service-${i}`}>
                <s.icon size={32} className="mx-auto mb-4 text-[#0d9488]" strokeWidth={1.2} />
                <h3 className="text-lg mb-2">{s.title}</h3>
                <p className="text-gray-400 text-xs font-['Manrope',sans-serif]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notre Engagement */}
      {showPrestations && (
      <div className="py-24">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src="/images/traiteur/services.jpg" alt="Service traiteur" className="w-full rounded-lg shadow-lg" />
            <div className="absolute bottom-0 right-0 bg-[#0d9488] text-white px-6 py-4 rounded-tl-lg">
              <span className="text-3xl font-light italic block leading-none">15+</span>
              <span className="text-[10px] tracking-[2px] uppercase font-['Manrope',sans-serif]">ans d'experience</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-[#0d9488] mb-3">Notre Engagement</p>
            <h2 className="text-4xl font-light mb-1">Un service</h2>
            <h2 className="text-4xl italic mb-6">sur mesure</h2>
            <p className="text-gray-500 text-base font-['Manrope',sans-serif] leading-relaxed mb-4">
              Chaque evenement est unique, c'est pourquoi nous creons des prestations personnalisees qui refletent vos gouts et vos envies. Du cocktail intime au grand banquet, nous nous adaptons a toutes vos demandes.
            </p>
            <p className="text-gray-400 text-sm font-['Manrope',sans-serif] leading-relaxed mb-6">
              Notre equipe se deplace dans toute la region des Pyrenees-Orientales pour vous offrir une prestation de qualite, ou que vous soyez.
            </p>
            <button onClick={() => nav('/site/traiteur/prestations')}
              className="text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase text-[#0d9488] flex items-center gap-2 hover:gap-3 transition-all">
              Decouvrir nos prestations <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Nos Plateaux */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-[#0d9488] mb-3">Nos Plateaux</p>
            <h2 className="text-4xl font-light mb-1">Des plateaux</h2>
            <h2 className="text-4xl italic mb-6">savoureux</h2>
            <p className="text-gray-500 text-base font-['Manrope',sans-serif] leading-relaxed mb-6">
              Pour vos repas de famille, reunions ou evenements, decouvrez notre selection de plateaux repas prepares avec des produits frais et locaux.
            </p>
            <ul className="space-y-2 mb-8">
              {['Plateaux charcuterie & fromages', 'Plateaux de fruits de mer', 'Plateaux aperitifs', 'Plateaux desserts'].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-500 text-sm font-['Manrope',sans-serif]">
                  <span className="w-2 h-2 rounded-full bg-[#0d9488]" /> {item}
                </li>
              ))}
            </ul>
            <button onClick={() => nav('/site/traiteur/plateaux')}
              className="text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase text-[#0d9488] flex items-center gap-2 hover:gap-3 transition-all">
              Voir nos plateaux <ArrowRight size={14} />
            </button>
          </div>
          <div>
            <img src="/images/traiteur/plateaux/plateaux1.jpg" alt="Plateaux repas" className="w-full rounded-lg shadow-lg" />
          </div>
        </div>
      </div>

      {/* Galerie Preview */}
      <div className="py-24">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">Galerie</p>
          <h2 className="text-center text-4xl font-light italic mb-12">Nos realisations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[3, 4, 6, 8].map(n => (
              <img key={n} src={`/images/traiteur/gallery/gallery${n}.jpg`} alt={`Galerie ${n}`}
                className="w-full aspect-[4/5] object-cover rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
                onClick={() => nav('/site/traiteur/galerie')}
              />
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => nav('/site/traiteur/galerie')}
              className="bg-[#0d9488] text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#0b8278] transition-colors">
              Voir toute la galerie
            </button>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-[#0d9488] text-white text-center">
        <h2 className="text-4xl font-light mb-4">Un projet en tete ?</h2>
        <p className="text-white/60 text-base font-['Manrope',sans-serif] mb-8 max-w-lg mx-auto">
          Contactez-nous pour discuter de votre evenement et recevoir un devis personnalise gratuit.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => nav('/site/traiteur/contact')}
            className="border-2 border-white text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors">
            Demander un devis gratuit
          </button>
          <a href="tel:0468380698"
            className="border-2 border-white/30 text-white/70 px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
            <Phone size={14} /> 04 68 38 06 98
          </a>
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="traiteur" />
    </div>
  );
}

// ══════ TRAITEUR PRESTATIONS ══════
export function TraiteurPrestations() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="traiteur-prestations">
      <SEO title="Prestations Traiteur" description="Decouvrez les prestations traiteur du Mas. Mariages, baptemes, anniversaires, repas d'entreprise. Service complet en Pays Catalan." path="/site/traiteur/prestations" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="traiteur" />

      <div className="relative h-[50vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/traiteur/services.jpg)' }} />
        <div className="absolute inset-0 bg-[#0d9488]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">Le Traiteur du Mas</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">Nos Prestations</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-24 space-y-20">
        {[
          { icon: Heart, title: 'Evenements prives', desc: 'Celebrez les moments importants avec une cuisine d\'exception. Mariage, bapteme, anniversaire, communion... Chaque occasion possede sa formule et le Traiteur du Mas fait en sorte qu\'elle soit unique.', image: '/images/traiteur/slider1.jpg' },
          { icon: Briefcase, title: 'Evenements professionnels', desc: 'Le Traiteur du Mas s\'invite dans vos bureaux ! Pour les professionnels qui souhaitent partager un repas d\'affaires entre collaborateurs ou avec des clients, nous nous adaptons a vos besoins.', image: '/images/traiteur/slider2.jpg' },
          { icon: Star, title: 'Personnel forme et discret', desc: 'Pour vos evenements, nous disposons d\'un personnel forme et discret afin de servir vos hotes lors d\'une reception ou d\'un seminaire.', image: '/images/traiteur/slider3.jpg' },
        ].map((p, i) => (
          <div key={i} className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'md:[direction:rtl]' : ''}`}>
            <div className="md:[direction:ltr]">
              <img src={p.image} alt={p.title} className="w-full h-72 object-cover rounded-lg shadow-lg" />
            </div>
            <div className="md:[direction:ltr]">
              <p.icon size={28} className="text-[#0d9488] mb-4" strokeWidth={1.2} />
              <h2 className="text-2xl font-light mb-4">{p.title}</h2>
              <p className="text-gray-500 text-sm font-['Manrope',sans-serif] leading-relaxed">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="py-20 bg-[#0d9488] text-white text-center">
        <h2 className="text-4xl font-light mb-4">Votre evenement sur mesure</h2>
        <p className="text-white/60 text-base font-['Manrope',sans-serif] mb-8">Notre equipe s'adapte a vos envies et a votre budget</p>
        <button onClick={() => nav('/site/traiteur/contact')}
          className="border-2 border-white text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors">
          Demander un devis
        </button>
      </div>

      <WhatsAppButton />
      <SiteFooter section="traiteur" />
    </div>
  );
}

// ══════ TRAITEUR PLATEAUX ══════
export function TraiteurPlateaux() {
  const [plateaux, setPlateaux] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/site/plateaux`).then(r => r.json()).then(setPlateaux).catch(() => {});
  }, []);

  const defaultPlateaux = [
    { name: "L'Aperitif", image_url: '/images/traiteur/aperitif.jpg', description: 'Assortiment aperitif a partager' },
    { name: 'Le Gourmet', image_url: '/images/traiteur/gourmet.png', description: 'Plateau gourmet raffine' },
    { name: 'Le Gourmand', image_url: '/images/traiteur/gourmand.png', description: 'Plateau gourmand genereux' },
    { name: 'Fruits de Mer', image_url: '/images/traiteur/fruits_mer.png', description: 'Selection de fruits de mer frais' },
    { name: 'Le Fromage', image_url: '/images/traiteur/fromage.png', description: 'Plateau de fromages affines' },
    { name: 'Le Sucre', image_url: '/images/traiteur/sucre.png', description: 'Assortiment de douceurs sucrees' },
    { name: 'Le Fruit', image_url: '/images/traiteur/fruit.png', description: 'Corbeille de fruits frais' },
    { name: 'Le Brunch', image_url: '/images/traiteur/brunch.png', description: 'Formule brunch complete' },
  ];

  const items = plateaux.length > 0 ? plateaux : defaultPlateaux;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="traiteur-plateaux">
      <SEO title="Plateaux Repas" description="Plateaux repas traiteur Le Mas. Livraison entreprises et particuliers a Saint-Laurent-de-la-Salanque et environs." path="/site/traiteur/plateaux" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="traiteur" />

      <div className="relative h-[50vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/traiteur/carte_intro.jpg)' }} />
        <div className="absolute inset-0 bg-[#0d9488]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">Le Traiteur du Mas</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">Nos Plateaux</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-24">
        <p className="text-center text-gray-500 text-base font-['Manrope',sans-serif] mb-16 max-w-xl mx-auto">
          Decouvrez des produits de qualite soigneusement selectionnes et presentes. Amateur de sucre ou de sale, votre bonheur est servi sur un plateau.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {items.map((p, i) => (
            <div key={i} className="group cursor-pointer" data-testid={`plateau-${i}`}>
              <div className="aspect-square rounded-lg overflow-hidden mb-3 border border-gray-100 group-hover:shadow-lg transition-shadow">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <h3 className="text-base text-center group-hover:text-[#0d9488] transition-colors">{p.name}</h3>
              <p className="text-gray-400 text-xs text-center font-['Manrope',sans-serif] mt-1">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="py-20 bg-[#0d9488] text-white text-center">
        <h2 className="text-4xl font-light mb-4">A emporter ou a livrer</h2>
        <p className="text-white/60 text-base font-['Manrope',sans-serif] mb-8">Directement chez vous</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => nav('/site/traiteur/contact')}
            className="border-2 border-white text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors">
            Commander
          </button>
          <a href="tel:0468380698"
            className="border-2 border-white/30 text-white/70 px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
            <Phone size={14} /> 04 68 38 06 98
          </a>
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="traiteur" />
    </div>
  );
}

// ══════ TRAITEUR GALERIE ══════
export function TraiteurGalerie() {
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/site/gallery?section=traiteur`).then(r => r.json()).then(setPhotos).catch(() => {});
  }, []);

  const defaultPhotos = Array.from({ length: 34 }, (_, i) => ({ id: `t-gal-${i+1}`, url: `/images/traiteur/gallery/gallery${i+1}.jpg` }));
  const items = photos.length > 0 ? photos : defaultPhotos;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="traiteur-galerie">
      <SEO title="Galerie Traiteur" description="Photos de nos prestations traiteur. Mariages, buffets, plateaux repas en Pays Catalan." path="/site/traiteur/galerie" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="traiteur" />

      <div className="pt-24 pb-16">
        <div className="text-center py-12">
          <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">Le Traiteur du Mas</p>
          <h1 className="text-4xl md:text-5xl font-light">Galerie Photos</h1>
        </div>
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((p, i) => (
              <div key={p.id || i}
                className="aspect-square bg-cover bg-center rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundImage: `url(${p.url})` }}
                onClick={() => setLightbox(p.url)}
                data-testid={`traiteur-photo-${i}`}
              />
            ))}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Photo" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      )}

      <WhatsAppButton />
      <SiteFooter section="traiteur" />
    </div>
  );
}

// ══════ TRAITEUR CONTACT ══════
export function TraiteurContact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', event_type: '', event_date: '', guests: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/api/site/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, section: 'traiteur' })
      });
      setSent(true);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="traiteur-contact">
      <SEO title="Devis Traiteur" description="Demandez un devis traiteur gratuit au Mas. Mariages, evenements, repas d'entreprise. 04 68 38 06 98." path="/site/traiteur/contact" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="traiteur" />

      <div className="relative h-[40vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/traiteur/contact_intro.jpg)' }} />
        <div className="absolute inset-0 bg-[#0d9488]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">Organisons votre evenement</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">Contactez-nous</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-20 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div>
            <p className="text-[#0d9488] text-xs font-['Manrope',sans-serif] tracking-wider uppercase mb-2">De quoi ravir toutes les papilles !</p>
            <p className="text-gray-500 text-sm font-['Manrope',sans-serif] leading-relaxed">
              Nous serons ravis de repondre a toutes vos questions et de vous accompagner dans l'organisation de votre evenement.
            </p>
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><Phone size={16} className="text-[#0d9488]" /> Telephone</p>
            <a href="tel:0468380698" className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6 hover:text-[#0d9488]">04 68 38 06 98</a>
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><MapPin size={16} className="text-[#0d9488]" /> Adresse</p>
            <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">6 Av. Marechal Joffre, 66250 Saint-Laurent-de-la-Salanque</p>
          </div>
          <div className="pt-4">
            <img src="/images/traiteur/team.jpg" alt="L'equipe" className="w-full rounded-lg shadow-lg" />
          </div>
        </div>
        <div>
          {sent ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <h3 className="text-3xl font-light mb-3 text-[#0d9488]">Merci !</h3>
              <p className="text-gray-500 text-sm font-['Manrope',sans-serif]">Nous vous recontacterons dans les plus brefs delais pour organiser votre evenement.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="traiteur-contact-form">
              <select value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] text-gray-600 focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20 appearance-none bg-white"
                data-testid="event-type-select">
                <option value="">Type d'evenement</option>
                <option value="mariage">Mariage</option>
                <option value="bapteme">Bapteme</option>
                <option value="anniversaire">Anniversaire</option>
                <option value="seminaire">Seminaire</option>
                <option value="plateau">Commande de plateau</option>
                <option value="autre">Autre</option>
              </select>
              <div className="grid grid-cols-2 gap-4">
                <input type="date" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20" />
                <input type="number" placeholder="Nombre d'invites" value={form.guests} onChange={e => setForm(p => ({ ...p, guests: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20" />
              </div>
              <input type="text" placeholder="Nom" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20" data-testid="contact-name" />
              <input type="email" placeholder="Email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20" data-testid="contact-email" />
              <input type="tel" placeholder="Telephone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20" data-testid="contact-phone" />
              <textarea placeholder="Decrivez votre projet..." required rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#0d9488] focus:outline-none focus:ring-1 focus:ring-[#0d9488]/20 resize-none" data-testid="contact-message" />
              <button type="submit" className="w-full bg-[#0d9488] text-white py-3.5 rounded-lg text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#0b8278] transition-colors" data-testid="contact-submit">
                Envoyer ma demande
              </button>
            </form>
          )}
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="traiteur" />
    </div>
  );
}
